import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import toast from 'react-hot-toast';
import { usePayoutConfig, useUpdatePayoutConfig } from '../../hooks/usePayout';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

const NUMBER_FIELDS = [
  'PaymentGatewayFeePercent', 'InfrastructurePercent', 'MarketingPercent', 'OverheadPercent',
  'ContentPoolPercent', 'PlatformProfitPercent',
  'WatchTimeWeight', 'CompletionWeight', 'AcquisitionWeight', 'RetentionWeight', 'RatingWeight',
  'MinQualifiedSeconds', 'MinQualifiedRuntimePercent', 'MaxCountedRewatchesPerUserPerMonth',
  'AcquisitionWindowDays', 'MovieCompletionThresholdPercent', 'SeriesEpisodeCompletionThresholdPercent',
  'AutoplayIdleGapSeconds', 'DiscoveryWindowDays', 'DiscoveryMultiplierPercent',
  'TierAGuaranteedMinimumNaira', 'TierBGuaranteedMinimumNaira', 'TierCGuaranteedMinimumNaira',
  'FloorPayoutPerActiveTitleNaira',
];

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function Field({ label, hint, value, onChange, step = '1', suffix }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          step={step}
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
        />
        {suffix && <span className="shrink-0 text-xs text-muted-foreground/70">{suffix}</span>}
      </div>
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground/70">{hint}</span>}
    </div>
  );
}

function Toggle({ label, hint, checked, onChange }) {
  return (
    <div>
      <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="size-4 rounded border-white/20 bg-white/5 accent-primary"
        />
        {label}
      </label>
      {hint && <span className="mt-1 block text-[11px] text-muted-foreground/70">{hint}</span>}
    </div>
  );
}

function SectionCard({ title, children, footer }) {
  return (
    <div className="mb-6 rounded-xl bg-white/[0.03] p-5 ring-1 ring-foreground/[0.06] sm:p-6">
      <h2 className="mb-4 text-sm font-semibold text-foreground">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {children}
      </div>
      {footer && <div className="mt-4">{footer}</div>}
    </div>
  );
}

function SumBadge({ sum, target = 100 }) {
  const ok = Math.abs(sum - target) < 0.01;
  return (
    <span className={`text-sm font-semibold ${ok ? 'text-emerald-400' : 'text-destructive'}`}>
      Total: {sum}% {ok ? '✓' : `(must equal ${target}%)`}
    </span>
  );
}

export default function AdminPayoutConfigPage() {
  usePageTitle('Payout Config · Kast');
  const { data: config, isLoading, error } = usePayoutConfig();
  const updateMutation = useUpdatePayoutConfig();
  const [form, setForm] = useState(null);

  useEffect(() => {
    if (config) setForm(config);
  }, [config]);

  const set = (field) => (value) => setForm((f) => ({ ...f, [field]: value }));

  if (isLoading || !form) {
    return (
      <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
        {error ? (
          <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load payout configuration.')}</EmptyState>
        ) : (
          <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
        )}
      </div>
    );
  }

  const revenueSum = ['PaymentGatewayFeePercent', 'InfrastructurePercent', 'MarketingPercent', 'OverheadPercent', 'ContentPoolPercent', 'PlatformProfitPercent']
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);
  const weightSum = ['WatchTimeWeight', 'CompletionWeight', 'AcquisitionWeight', 'RetentionWeight', 'RatingWeight']
    .reduce((s, k) => s + (parseFloat(form[k]) || 0), 0);

  const handleSave = async () => {
    // Coerce any blank inputs back to 0 before sending.
    const payload = { ...form };
    NUMBER_FIELDS.forEach((f) => { if (payload[f] === '') payload[f] = 0; });

    try {
      await updateMutation.mutateAsync(payload);
      toast.success('Payout configuration saved.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save configuration.'));
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex items-center gap-3">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
          <Icon icon="ph:sliders-horizontal-bold" className="size-5" />
        </span>
        <div>
          <h1 className="text-lg font-semibold text-foreground">Payout Configuration</h1>
          <p className="text-xs text-muted-foreground">
            Every knob in the Cast monetization engine. Changes apply to the next payout run you compute —
            already-finalized runs keep the config they were computed with.
          </p>
        </div>
      </div>

      <SectionCard
        title="Step 1 — Monthly revenue allocation"
        footer={<SumBadge sum={Math.round(revenueSum * 100) / 100} />}
      >
        <Field label="Payment gateway / VAT" suffix="%" value={form.PaymentGatewayFeePercent} onChange={set('PaymentGatewayFeePercent')} step="0.1" />
        <Field label="Infrastructure" suffix="%" value={form.InfrastructurePercent} onChange={set('InfrastructurePercent')} step="0.1" />
        <Field label="Marketing / acquisition" suffix="%" value={form.MarketingPercent} onChange={set('MarketingPercent')} step="0.1" />
        <Field label="Staff / admin / overhead" suffix="%" value={form.OverheadPercent} onChange={set('OverheadPercent')} step="0.1" />
        <Field label="Producer content pool" suffix="%" value={form.ContentPoolPercent} onChange={set('ContentPoolPercent')} step="0.1" />
        <Field label="Platform profit" suffix="%" value={form.PlatformProfitPercent} onChange={set('PlatformProfitPercent')} step="0.1" />
      </SectionCard>

      <SectionCard
        title="Step 2 — Title Performance Score weights"
        footer={<SumBadge sum={Math.round(weightSum * 100) / 100} />}
      >
        <Field label="Watch Time Share" suffix="%" value={form.WatchTimeWeight} onChange={set('WatchTimeWeight')} step="0.1" />
        <Field label="Completion Rate" suffix="%" value={form.CompletionWeight} onChange={set('CompletionWeight')} step="0.1" />
        <Field label="Subscriber Acquisition" suffix="%" value={form.AcquisitionWeight} onChange={set('AcquisitionWeight')} step="0.1" />
        <Field label="Retention Impact" suffix="%" value={form.RetentionWeight} onChange={set('RetentionWeight')} step="0.1" />
        <Field label="User Rating" suffix="%" value={form.RatingWeight} onChange={set('RatingWeight')} step="0.1" />
      </SectionCard>

      <SectionCard title={'Qualification rules ("Qualified Minutes", not raw clicks)'}>
        <Field label="Minimum watch time" suffix="seconds" value={form.MinQualifiedSeconds} onChange={set('MinQualifiedSeconds')} />
        <Field label="Minimum % of runtime" hint="0 = disabled, rely on seconds only" suffix="%" value={form.MinQualifiedRuntimePercent} onChange={set('MinQualifiedRuntimePercent')} step="0.1" />
        <Field label="Max counted rewatches" hint="per user, per title, per month" value={form.MaxCountedRewatchesPerUserPerMonth} onChange={set('MaxCountedRewatchesPerUserPerMonth')} />
        <Field label="Acquisition window" hint="days after signup a watch still counts" suffix="days" value={form.AcquisitionWindowDays} onChange={set('AcquisitionWindowDays')} />
        <Field label="Movie completion threshold" suffix="% of runtime" value={form.MovieCompletionThresholdPercent} onChange={set('MovieCompletionThresholdPercent')} step="0.1" />
        <Field label="Series episode completion threshold" suffix="% of episode" value={form.SeriesEpisodeCompletionThresholdPercent} onChange={set('SeriesEpisodeCompletionThresholdPercent')} step="0.1" />
        <Field label="Autoplay idle gap" hint="heartbeat silence before treated as idle" suffix="seconds" value={form.AutoplayIdleGapSeconds} onChange={set('AutoplayIdleGapSeconds')} />
      </SectionCard>

      <SectionCard title="Discovery protection">
        <Toggle label="Enabled" checked={form.DiscoveryProtectionEnabled} onChange={set('DiscoveryProtectionEnabled')} />
        <Field label="Discovery window" suffix="days after approval" value={form.DiscoveryWindowDays} onChange={set('DiscoveryWindowDays')} />
        <Field label="TPS boost" suffix="%" value={form.DiscoveryMultiplierPercent} onChange={set('DiscoveryMultiplierPercent')} step="0.1" />
      </SectionCard>

      <SectionCard title="Catalog tier guarantees (₦ / title / month)">
        <Field label="Tier A — Premium Originals" suffix="₦" value={form.TierAGuaranteedMinimumNaira} onChange={set('TierAGuaranteedMinimumNaira')} step="1000" />
        <Field label="Tier B — Licensed Hits" hint="usually 0 — algorithmic only" suffix="₦" value={form.TierBGuaranteedMinimumNaira} onChange={set('TierBGuaranteedMinimumNaira')} step="1000" />
        <Field label="Tier C — Indie / New Producers" hint="discovery stipend" suffix="₦" value={form.TierCGuaranteedMinimumNaira} onChange={set('TierCGuaranteedMinimumNaira')} step="1000" />
      </SectionCard>

      <SectionCard title={'"Floor + Variable" launch strategy'}>
        <Toggle label="Enabled" hint="every active title gets at least the floor amount" checked={form.FloorPayoutEnabled} onChange={set('FloorPayoutEnabled')} />
        <Field label="Floor per active title" suffix="₦ / month" value={form.FloorPayoutPerActiveTitleNaira} onChange={set('FloorPayoutPerActiveTitleNaira')} step="1000" />
      </SectionCard>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={updateMutation.isPending} className="gap-1.5">
          <Icon icon="ph:floppy-disk-bold" />
          {updateMutation.isPending ? 'Saving…' : 'Save Configuration'}
        </Button>
      </div>
    </div>
  );
}
